import {Component, Directive, Inject, Injector, Input, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {
    combineLatest,
    concat,
    distinctUntilChanged,
    filter,
    Observable,
    of,
    ReplaySubject,
    Subject,
    Subscription,
    switchMap,
    take,
    tap
} from "rxjs";
import {autocompleteRequest, MultiselectSettings, State, ToastMessage} from "../interfaces/admin.interface";
import {BsLocaleService} from "ngx-bootstrap/datepicker";
import {TranslateService} from "@ngx-translate/core";
import {AbstractControl, ControlValueAccessor, NgControl, NgForm, ValidationErrors, Validator} from "@angular/forms";
import {Command} from "./viewTracker.module";
import swal from "sweetalert2";
import {EmitEvent, EventBus, Events} from "../services/eventBus.service";
import {GenesisComponent} from "./genesis.component";
import {NgSelectComponent} from "@ng-select/ng-select";
import {catchError} from "rxjs/operators";
import value from "*.json";

@Component({
    template: '',
})
export abstract class BaseComponent extends GenesisComponent implements OnDestroy {

    protected eventBus: EventBus
    protected localeService: BsLocaleService
    protected translationService: TranslateService

    @ViewChild('form')
    form: NgForm

    protected subscriptions: Subscription[] = [];
    protected command: Command<any>;

    protected multiselectSettings_base: MultiselectSettings = {
        singleSelection: true,
        text: "Select",
        searchPlaceholderText: "Search",
        enableSearchFilter: true,
        classes: "single-selection",
        badgeShowLimit: 3,
    };

    constructor(
        injector: Injector,
    ) {
        super()
        this.eventBus = injector.get(EventBus)
        this.localeService = injector.get(BsLocaleService)
        this.translationService = injector.get(TranslateService)
        if (this.localeService && this.translationService)
            this.localeService.use(this.translationService.currentLang);
    }

    onSubmit(): void {
        console.log('form valid:', this.form.valid);
        console.log('form controls:', this.form.controls);
        Object.keys(this.form.controls).forEach(key => {
            const ctrl = this.form.controls[key];

            console.log(key, {
                value: ctrl.value,
                valid: ctrl.valid,
                status: ctrl.status,
                errors: ctrl.errors,
                enabled: ctrl.enabled,
                disabled: ctrl.disabled
            });

        });

        if (this.form.valid) {
            this.submitImpl()
        } else {
            const notify = this.triggerValidation()
            if(notify)
                this.eventBus.emit(new EmitEvent(Events.ValidationFailed))
        }
    }

    protected triggerValidation() {
        Object.keys(this.form.controls).forEach(field => { // {1}
            const control = this.form.controls[field];            // {2}
            control.markAsTouched({onlySelf: true});       // {3}
        });
        return true
    }

    protected submitImpl() {
    }

    public get new() {
        return this.command && this.command != null && this.command.new
    }

    public get edit() {
        return this.command && this.command != null && !this.command.new
    }

    protected add(subscription: Subscription) {
        this.subscriptions.push(subscription)
    }

    protected defaultToastMessage(): ToastMessage {
        return {
            elementList: null,
            multiselect: null,
            toggle_active_ok: null,
            toggle_active_er: null,
            duplicate_ok: null,
            duplicate_er: null,
            delete_er: null,
            delete_ok: null,
            invite_ok: null,
            invite_er: null
        };
    }

    protected initMultiselect() {
        return Object.assign({}, this.multiselectSettings_base);
    }

    protected askForAction(title: string, text: string, confirmFn: () => void, cancelFn?: () => void) {
        swal.fire({
            title: title,
            text: text,
            icon: "warning",
            showCancelButton: true,
            showConfirmButton: true,
        }).then(function (result) {
            if (result.isConfirmed) {
                confirmFn()
            } else if (cancelFn) {
                cancelFn()
            }
        })
    }


    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe())
    }
}

@Component({
    template: '',
})
export abstract class BaseSelect<T> implements OnInit {

    @ViewChild("select")
    select: NgSelectComponent
    autocomplete: boolean = false;

    entries$: Observable<any>;
    entries: T[];
    loading = false;
    input$ = new Subject<string>();
    selectedEntries: any;

    _readonly: boolean = false

    protected _companyId: number = null
    loadNum = 0

    constructor(@Inject(String) autocomplete: boolean = false) {
        this.autocomplete = autocomplete;
    }

    ngOnInit() {
        this.load()
    }

    load() {
        this.entries$ = concat(
            of([]), // default items
            this.input$.pipe(
                distinctUntilChanged(),
                filter(term => term && term.trim().length > 2),
                tap(() => (this.loading = true)),
                switchMap(term => term ? this.executeRequest(term) : null,
                ),
            ),
        );
    }

    abstract executeRequest(term? : string): Observable<any>

    request() {
        const req = autocompleteRequest()
        req.filters = { }
        this.fill(req.filters)
        return req;
    }

    processResponse(res: any) {
        const recs = res.data as T[]
        const results = recs.filter(r => r['company'] == this._companyId)
        this.entries = results
        this.selectedEntries = results[0]['id']
        return results
    }

    abstract fill(filters: any)

    fillBasicFilters(filters: any, prop: string) {
        filters[prop] = {
            state: State.ALL,
            company: [this._companyId ? this._companyId : null]
        }
    }

    @Input() set company(companyId: number) {
        this._companyId = companyId
        this.selectedEntries = null
        // this.disabled = companyId == null
        if(this.select) {
            if(this.autocomplete) {
                this.select.itemsList.setItems([])
            }
            else {
                this.input$.next(''+this.loadNum++)
            }
        }
    }


    get company(): number {
        return this._companyId;
    }


    get disabled(): boolean {
        return this.select.readonly
    }

    @Input() set disabled(value: boolean) {
        if(this.select) {
            this.select.readonly = value
        }
        this._readonly = value
    }

    public focus() {
        this.select.focus()
    }
}



@Directive()
export abstract class BaseListSelect<T> implements OnInit, ControlValueAccessor, Validator {

    @ViewChild('select') select: NgSelectComponent;

    entries: T[] = [];
    loading = false;
    selectedValue: any = null;

    private _required = false;
    private _companyId: number = null;

    private entriesLoaded$ = new ReplaySubject<void>(1);
    private pendingValue$ = new ReplaySubject<any>(1);

    @Input() disabled = false;

    private propagateChange: (val: any) => void = () => {};
    private propagateTouched: () => void = () => {};
    private onValidatorChange: () => void = () => {};

    constructor(private injector: Injector) {
    }

    /** Override in subclass if your identity key is not 'id' */
    compareItems = (a: any, b: any): boolean => {
        return a && b && a.id === b.id;
    };

    // --- Inputs ---

    @Input()
    get required(): boolean { return this._required; }
    set required(value: boolean | string | undefined) {
        this._required = value != null && value !== false && `${value}` !== 'false';
        this.onValidatorChange();
    }

    @Input()
    get company(): number { return this._companyId; }
    set company(value: number) {
        if (this._companyId === value) return;

        this._companyId = value;

        if (value != null) {
            this.load();
        } else {
            this.entries = [];
            this.selectedValue = null;
            this.propagateChange(null);
        }
    }
    // --- Lifecycle ---

    ngOnInit(): void {
        this.syncValueAfterLoad();
    }

    // --- Loading ---

    load(): void {
        this.loading = true;
        this.fetchEntries().pipe(
            catchError(() => of([])),
            tap(() => (this.loading = false)),
        ).subscribe(entries => {
            this.entries = entries;
            this.entriesLoaded$.next();
        });
    }

    abstract fetchEntries(): Observable<T[]>;

    // --- Value sync ---

    private syncValueAfterLoad(): void {
        combineLatest([this.entriesLoaded$, this.pendingValue$])
            .pipe(take(1))
            .subscribe(([_, value]) => {
                this.selectedValue = value;
            });
    }

    // --- ControlValueAccessor ---

    writeValue(val: any): void {
        this.pendingValue$.next(val);
        if (this.entries.length > 0) {
            this.selectedValue = val;
        }
    }

    registerOnChange(fn: any): void { this.propagateChange = fn; }
    registerOnTouched(fn: any): void { this.propagateTouched = fn; }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onSelectionChange(val: any): void {
        this.selectedValue = val;
        this.propagateChange(val);
    }

    onBlur(): void {
        this.propagateTouched();
    }

    // --- Validator ---

    validate(control: AbstractControl): ValidationErrors | null {
        if (!this._required) return null;
        return !this.selectedValue ? { nonEmptyList: true } : null;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    // --- Error display: reads from the actual form control ---

    get showError(): boolean {
        const ngControl = this.injector.get(NgControl, null);
        if (!ngControl?.control) return false;
        return ngControl.control.invalid && (ngControl.control.dirty || ngControl.control.touched);
    }
}